/**
 * §P.7 · Las sedes del club.
 *
 * ## Una sede nace sin coordenadas, a propósito
 *
 * Las pone después el geocodificador, que lee las sedes activas sin latitud.
 * Pedírselas al club sería pedirle un dato que no tiene, y aceptarlas a ojo
 * llenaría el buscador de puntos inventados. Por eso el formulario de alta pide
 * dirección y no números.
 *
 * ## La precisión importa más de lo que parece
 *
 * Una sede situada **a nivel de ciudad cae en el centro del municipio**. Decirle a
 * una madre que ese gimnasio está «a 400 m» cuando está a cuatro kilómetros quema
 * el directorio entero, así que la precisión se pinta con color y con la frase de
 * qué significa.
 *
 * ## Mover el punto a mano es para siempre
 *
 * `directory.geocode_venues` **nunca sobrescribe** un punto con `geocode_source:
 * 'manual'`, porque si el maestro lo movió sabe más que el geocodificador — él sabe
 * que la entrada está por el callejón de atrás—. Es la única ruta del backend que
 * lo hace, así que la pantalla lo dice **antes** y no después.
 *
 * Y **las dos coordenadas van juntas**: media coordenada no sitúa nada y rompe la
 * búsqueda por cercanía (`422 incomplete_coordinates`).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { MapaDeSede, type Enfoque } from '@/club/ajustes/MapaDeSede';
import { BuscarEnElMapa } from '@/club/ajustes/BuscarEnElMapa';

/** Cuánto acercar según lo que encontró el buscador: la puerta, la calle, la zona. */
const ZOOM = { exacta: 18, calle: 17, codigo_postal: 15, ciudad: 13 } as const;
import { fechaYHora } from '@/shared/ui/formato';
import {
  aCoordenada,
  coordenadasCompletas,
  direccionDeSede,
  PRECISIONES,
  puntoPuestoAMano,
  tienePunto,
  type Sede,
} from '@/club/ajustes/tipos';

interface Campos {
  name: string;
  address_line: string;
  postal_code: string;
  city: string;
  province: string;
  latitud: string;
  longitud: string;
  is_primary: boolean;
}

const desde = (s: Sede | null): Campos => ({
  name: s?.name ?? '',
  address_line: s?.address_line ?? '',
  postal_code: s?.postal_code ?? '',
  city: s?.city ?? '',
  province: s?.province ?? '',
  latitud: s?.latitude === null || s === null ? '' : String(s.latitude),
  longitud: s?.longitude === null || s === null ? '' : String(s.longitude),
  is_primary: s?.is_primary ?? false,
});

const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function Sedes({
  club,
  puedeEditar,
  puedeCerrar,
  empezarCreando = false,
  alCrear,
}: {
  club: string;
  puedeEditar: boolean;
  /** Cerrar una sede es solo del dueño: el contrato declara `own`. */
  puedeCerrar: boolean;
  /** Abrir directamente el alta (`?nuevo=1`). */
  empezarCreando?: boolean;
  /** Al crear una sede, volver adonde se estaba (`?volver=`, 03-11-2026). */
  alCrear?: (() => void) | undefined;
}) {
  const cache = useQueryClient();
  const [editando, setEditando] = useState<Sede | null | undefined>(
    empezarCreando ? null : undefined
  );
  const [campos, setCampos] = useState<Campos>(() => desde(null));
  const [enfoque, setEnfoque] = useState<Enfoque | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [cerrando, setCerrando] = useState<Sede | null>(null);

  const sedes = useQuery({
    queryKey: ['sedes', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).venues.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Sede[];
    },
  });

  const abrir = (s: Sede | null) => {
    setEditando(s);
    setCampos(desde(s));
    setEnfoque(null);
    setProblema(null);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const lat = aCoordenada(campos.latitud, 90);
      const lon = aCoordenada(campos.longitud, 180);

      if (lat === undefined || lon === undefined) {
        throw new ErrorDeApi({
          title: 'Esas coordenadas no se entienden',
          status: 400,
          detail:
            'La latitud va de −90 a 90 y la longitud de −180 a 180, con punto o coma decimal.',
        });
      }

      if (!coordenadasCompletas(lat, lon)) {
        // El servidor responde `422 incomplete_coordinates`; mejor no mandarlo.
        throw new ErrorDeApi({
          title: 'Faltan la mitad de las coordenadas',
          status: 400,
          detail:
            'Las dos van juntas o ninguna: media coordenada no sitúa nada y rompe la búsqueda por cercanía.',
        });
      }

      const comun = {
        name: campos.name.trim(),
        address_line: oNulo(campos.address_line),
        postal_code: oNulo(campos.postal_code),
        city: oNulo(campos.city),
        province: oNulo(campos.province),
        is_primary: campos.is_primary,
      };

      if (editando === null) {
        // Sin coordenadas al crear: las pone el geocodificador. Ver la cabecera.
        const r = await api.v1.clubs({ club }).venues.post(comun);
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return r.data;
      }

      // Las coordenadas **solo si se han movido**. Reenviar las que ya tenía al
      // guardar el nombre las marcaba como puestas a mano, y la sede dejaba de
      // situarse sola para siempre sin que nadie hubiera tocado el punto.
      const movido =
        lat !== (editando!.latitude ?? null) ||
        lon !== (editando!.longitude ?? null);
      const r = await api.v1
        .clubs({ club })
        .venues({ venueId: editando!.id })
        .patch({
          ...comun,
          ...(movido ? { latitude: lat, longitude: lon } : {}),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      const eraNueva = editando === null;
      setEditando(undefined);
      void cache.invalidateQueries({ queryKey: ['sedes', club] });
      void cache.invalidateQueries({ queryKey: ['club', club] });
      void cache.invalidateQueries({ queryKey: ['hoy', club] });
      // Venía de otra pantalla a la que le faltaba la sede: vuelve a ella.
      if (eraNueva) alCrear?.();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cerrar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.clubs({ club }).venues({ venueId: id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setCerrando(null);
      void cache.invalidateQueries({ queryKey: ['sedes', club] });
      void cache.invalidateQueries({ queryKey: ['club', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── Cerrar una sede ──────────────────────────────────────────────────────
  if (cerrando) {
    return (
      <div className="hueco hueco-confirmar">
        <h3>¿Cerrar «{cerrando.name}»?</h3>

        <ul className="lista-clara">
          <li>
            <strong>No se borra.</strong> Los grupos y las clases que se dieron
            ahí siguen apuntando a esta sede: sin ella, el histórico no se puede
            leer.
          </li>
          {cerrando.group_count > 0 && (
            <li>
              <strong>
                Tiene {cerrando.group_count}{' '}
                {cerrando.group_count === 1 ? 'grupo' : 'grupos'} dentro.
              </strong>{' '}
              Muévelos antes o el servidor no lo va a permitir.
            </li>
          )}
        </ul>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            disabled={cerrar.isPending}
            onClick={() => cerrar.mutate(cerrando.id)}
          >
            {cerrar.isPending ? 'Cerrando…' : 'Sí, cerrarla'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setCerrando(null);
              setProblema(null);
            }}
          >
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  // ── El formulario ────────────────────────────────────────────────────────
  if (editando !== undefined) {
    const creando = editando === null;
    const cambiar = (campo: keyof Campos, valor: string | boolean) =>
      setCampos((c) => ({ ...c, [campo]: valor }));

    const moviendoElPunto =
      !creando &&
      (campos.latitud !== (editando?.latitude?.toString() ?? '') ||
        campos.longitud !== (editando?.longitude?.toString() ?? ''));

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          guardar.mutate();
        }}
      >
        <h1>{creando ? 'Añadir una sede' : `Editar «${editando.name}»`}</h1>

        <div className="rejilla">
          <label>
            Cómo se llama
            <input
              required
              maxLength={160}
              placeholder="Sede principal"
              value={campos.name}
              onChange={(e) => cambiar('name', e.target.value)}
            />
          </label>

          <label>
            Dirección
            <input
              maxLength={200}
              placeholder="Calle Mayor 3"
              value={campos.address_line}
              onChange={(e) => cambiar('address_line', e.target.value)}
            />
          </label>

          <label>
            Código postal
            <input
              maxLength={10}
              placeholder="28801"
              value={campos.postal_code}
              onChange={(e) => cambiar('postal_code', e.target.value)}
            />
          </label>

          <label>
            Ciudad
            <input
              maxLength={100}
              placeholder="Alcalá de Henares"
              value={campos.city}
              onChange={(e) => cambiar('city', e.target.value)}
            />
          </label>

          <label>
            Provincia
            <input
              maxLength={100}
              placeholder="Madrid"
              value={campos.province}
              onChange={(e) => cambiar('province', e.target.value)}
            />
          </label>

          <label className="opcion">
            <input
              type="checkbox"
              checked={campos.is_primary}
              onChange={(e) => cambiar('is_primary', e.target.checked)}
            />
            Es la sede principal
          </label>
        </div>

        {creando ? (
          <div className="nota">
            <strong>No hace falta poner el punto del mapa.</strong> Lo busca
            Kiap a partir de la dirección. Si luego cae en el sitio equivocado,
            se mueve a mano desde aquí.
          </div>
        ) : (
          <>
            <h2 className="seccion">El punto en el mapa</h2>

            <p className="tenue">
              Busca la dirección, o pulsa en el mapa donde está la entrada y
              arrastra el marcador.
            </p>
            <BuscarEnElMapa
              club={club}
              direccionInicial={[
                campos.address_line,
                [campos.postal_code, campos.city].filter(Boolean).join(' '),
              ]
                .filter(Boolean)
                .join(', ')}
              alEncontrar={(p) => {
                setCampos((c) => ({
                  ...c,
                  latitud: String(p.latitude),
                  longitud: String(p.longitude),
                }));
                setEnfoque({
                  latitud: p.latitude,
                  longitud: p.longitude,
                  zoom: ZOOM[p.precision],
                  n: Date.now(),
                });
              }}
            />
            <MapaDeSede
              enfoque={enfoque}
              latitud={aCoordenada(campos.latitud, 90) ?? null}
              longitud={aCoordenada(campos.longitud, 180) ?? null}
              alMover={(lat, lon) =>
                setCampos((c) => ({
                  ...c,
                  latitud: String(lat),
                  longitud: String(lon),
                }))
              }
            />

            <div className="rejilla">
              <label>
                Latitud
                <input
                  inputMode="decimal"
                  placeholder="40,4816"
                  value={campos.latitud}
                  onChange={(e) => cambiar('latitud', e.target.value)}
                />
              </label>

              <label>
                Longitud
                <input
                  inputMode="decimal"
                  placeholder="-3,3644"
                  value={campos.longitud}
                  onChange={(e) => cambiar('longitud', e.target.value)}
                />
              </label>
            </div>

            {/*
              El aviso solo si de verdad se está moviendo: es una decisión con
              consecuencia permanente y un cartel fijo no se lee.
            */}
            {moviendoElPunto ? (
              <div className="aviso" role="status">
                <strong>
                  Al mover el punto a mano, Kiap deja de buscarlo solo.
                </strong>{' '}
                Para siempre, y es lo correcto: si lo mueves es porque sabes
                algo que el mapa no sabe —que la entrada está por el callejón de
                atrás—. Pero si luego cambias la dirección, el punto no la
                seguirá: habrá que moverlo otra vez.
              </div>
            ) : (
              <p className="tenue">
                Lo normal es dejarlo como está: lo pone Kiap a partir de la
                dirección. Se toca cuando el punto cae mal.
              </p>
            )}
          </>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending
              ? 'Guardando…'
              : creando
                ? 'Añadir la sede'
                : 'Guardar la sede'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => setEditando(undefined)}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La lista ─────────────────────────────────────────────────────────────
  if (sedes.isPending) {
    return <Cargando filas={3} etiqueta="Cargando las sedes" />;
  }

  if (sedes.isError || !sedes.data) {
    return (
      <PanelError
        problema={aProblema(sedes.error)}
        reintentar={() => void sedes.refetch()}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Sedes</h1>
        {puedeEditar && (
          <button type="button" onClick={() => abrir(null)}>
            Añadir una sede
          </button>
        )}
      </header>

      <p className="tenue">
        Dónde se dan las clases. De la dirección sale el punto del buscador de
        clubes, así que conviene que esté fina.
      </p>

      {problema && <PanelError problema={problema} />}

      {sedes.data.length === 0 ? (
        <Vacio
          titulo="Todavía no hay sedes"
          explicacion="Un grupo se da en una sede, así que hace falta al menos una para poder crear horarios."
          {...(puedeEditar
            ? {
                accion: (
                  <button type="button" onClick={() => abrir(null)}>
                    Añadir la primera
                  </button>
                ),
              }
            : {})}
        />
      ) : (
        <ul className="sedes">
          {sedes.data.map((s) => {
            const p = s.geocode_precision
              ? PRECISIONES[s.geocode_precision]
              : undefined;
            const direccion = direccionDeSede(s);

            return (
              <li key={s.id} className={s.active ? undefined : 'fila-fuera'}>
                <div className="aviso-cabeza">
                  <strong>{s.name}</strong>
                  {s.is_primary && (
                    <span className="pastilla pastilla-info">Principal</span>
                  )}
                  {!s.active && <span className="pastilla">Cerrada</span>}
                </div>

                <p className="tenue">
                  {direccion === '' ? 'Sin dirección puesta' : direccion}
                </p>

                <div className="sede-mapa">
                  {!tienePunto(s) ? (
                    <span className="tenue">
                      Sin punto en el mapa todavía. Kiap lo busca esta noche.
                    </span>
                  ) : (
                    <>
                      {p && (
                        <span className={`pastilla ${p.clase}`}>
                          {p.etiqueta}
                        </span>
                      )}
                      {puntoPuestoAMano(s) && (
                        <span className="pastilla pastilla-info">
                          Puesto a mano
                        </span>
                      )}
                      {/*
                        La frase de qué significa la precisión, no solo la etiqueta:
                        «solo la ciudad» no dice nada a quien no sabe que eso pone el
                        punto en el centro del municipio.
                      */}
                      <span className="tenue">{p?.explicacion}</span>
                    </>
                  )}
                </div>

                <div className="aviso-pie">
                  <span className="tenue">
                    {s.group_count === 0
                      ? 'Ningún grupo'
                      : s.group_count === 1
                        ? '1 grupo'
                        : `${s.group_count} grupos`}
                    {s.geocoded_at
                      ? ` · situada ${fechaYHora(s.geocoded_at)}`
                      : ''}
                  </span>

                  {puedeEditar && (
                    <span className="tutor-acciones" style={{ margin: 0 }}>
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => abrir(s)}
                      >
                        Editar
                      </button>
                      {/* Cerrar es del dueño, y no de una sede ya cerrada. */}
                      {puedeCerrar && s.active && (
                        <button
                          type="button"
                          className="btn-plano"
                          onClick={() => {
                            setCerrando(s);
                            setProblema(null);
                          }}
                        >
                          Cerrar
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
